/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';

import { addToast } from 'core/notifications';
import { registerField } from 'core/forms';

import { Button } from 'uikit/buttons';

import FileListItem from '@/exceed/components/fileListItem';
import usePreviousState from '@/exceed/hooks/usePreviousState';

import { fileUploader } from '@/files';

import getFilesToRender from '../helpers/getFilesToRender';
import handleSourceMaterialMaxFileSizeError from '../helpers/handleSourceMaterialMaxFileSizeError';
import trackSourceMaterialFileUploadSize from '../helpers/trackSourceMaterialFileUploadSize';

import styles from '../styles/formFieldFileUpload.module.scss';

const FormFieldFileUpload = ({
  fieldKey,
  fieldId,
  schema: { setIsFileUploading, mimeTypes = [], maxUploadSizeInMb = 500 },
  updateField,
  value = [],
}) => {
  const inputRef = useRef();
  const [uploadMap, setUploadMap] = useState({});
  const prevUploadMap = usePreviousState(uploadMap);
  const files = useMemo(() => getFilesToRender({ uploadMap, value }), [uploadMap, value]);

  useEffect(() => {
    const completedUploads = Object.values(uploadMap).filter(({ status }) => status === 'complete');

    if (completedUploads.length > 0) {
      const newFiles = completedUploads.map(({ file }) => file);

      setUploadMap((map) => {
        const newUploadMap = { ...map };

        completedUploads.forEach(({ file }) => {
          delete newUploadMap[file.name];
        });

        return newUploadMap;
      });

      updateField(sortBy([...value, ...newFiles], 'name'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateField, uploadMap]);

  useEffect(() => {
    if ((!prevUploadMap || !Object.keys(prevUploadMap).length) && Object.keys(uploadMap).length) {
      setIsFileUploading(true);
    } else if (
      prevUploadMap &&
      Object.keys(prevUploadMap).length &&
      !Object.keys(uploadMap).length
    ) {
      setIsFileUploading(false);
    }
  }, [prevUploadMap, setIsFileUploading, uploadMap]);

  const onClick = () => inputRef.current.click();

  const onUpload = (file) => {
    const { name, size, type, ...metadata } = file;

    const fileUpload = {
      material_type: 'Resource',
      import_type: fieldKey,
      file_size: size,
      name,
      resource: {
        mime: type,
        metadata,
      },
      status: 'created',
    };

    if (files.some(({ name: fileName }) => fileName === name)) {
      addToast(i18n.t('file-already-exists'), 'error');
      return;
    }

    const maxFileSize =
      typeof maxUploadSizeInMb === 'number' ? maxUploadSizeInMb : maxUploadSizeInMb(type);

    if (size > maxFileSize * 1000000) {
      handleSourceMaterialMaxFileSizeError({ maxFileSize, mimeType: type });
      trackSourceMaterialFileUploadSize({
        fileSize: size,
        uploadStatus: 'failure',
      });
      return;
    }

    fileUploader(file, {
      start: () => {
        setUploadMap((map) => ({
          ...map,
          [fileUpload.name]: {
            status: 'uploading',
            progress: 0,
            file: fileUpload,
          },
        }));
      },
      progress: (percent) => {
        if (percent !== 100) {
          setUploadMap((map) => ({
            ...map,
            [fileUpload.name]: {
              ...map[fileUpload.name],
              progress: percent,
            },
          }));
        }
      },
      complete: (completionObj) => {
        fileUpload.resource.url = completionObj.url || completionObj;

        trackSourceMaterialFileUploadSize({
          fileSize: size,
          uploadStatus: 'success',
        });
        setUploadMap((map) => ({
          ...map,
          [fileUpload.name]: {
            status: 'complete',
            progress: 100,
            file: fileUpload,
          },
        }));
      },
      error: (errorMessage) => {
        setUploadMap((map) => {
          const newMap = { ...map };

          delete newMap[fileUpload.name];

          return newMap;
        });

        addToast(errorMessage, 'error');
      },
    });
  };

  const onRemove = (removeFile) => {
    const filteredValue = value.filter((file) => !isEqual(file, removeFile));
    updateField(filteredValue);
  };

  return (
    <div>
      <ul className={styles.list}>
        {files.map((item, index) => {
          const { file_size, material_type, name } = item;
          const upload = uploadMap[name] || null;

          return (
            <FileListItem
              // eslint-disable-next-line react/no-array-index-key
              key={`source_material_file_upload_${index}`}
              name={name}
              fileSize={file_size}
              materialType={material_type}
              onRemove={() => onRemove(item)}
              upload={upload}
            />
          );
        })}
      </ul>
      <input
        type="file"
        id={`file-upload-${fieldId}`}
        onChange={(event) => onUpload(event.target.files[0])}
        hidden
        className={styles.input}
        accept={mimeTypes.join(',')}
        ref={inputRef}
      />
      <Button variant="outlined" onClick={onClick}>
        <FileUploadOutlined sx={{ height: 18, width: 18 }} />
        {i18n.t('upload-file')}
      </Button>
    </div>
  );
};

FormFieldFileUpload.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    maxUploadSizeInMb: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
    mimeTypes: PropTypes.arrayOf(PropTypes.string),
    setIsFileUploading: PropTypes.func.isRequired,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      material_type: PropTypes.oneOf(['Course', 'Resource']).isRequired,
      import_type: PropTypes.oneOf(['file_upload', 'google_drive', 'one_drive']),
      file_size: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      resource: PropTypes.shape({
        external_id: PropTypes.string,
        mime: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        metadata: PropTypes.shape({}),
      }),
      status: PropTypes.oneOf([
        'created',
        'failure',
        'rag_token_requested',
        'request_file_transcript',
        'requested',
        'success',
      ]).isRequired,
    }),
  ),
};

registerField('FileUpload', FormFieldFileUpload);

export default FormFieldFileUpload;
